import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { TLessonBase } from '@onesource/schemas';
import { LessonStatus } from '@onesource/schemas';
import { useGetCategories, useGetSubLessons } from '@onesource/services';
import { SubLessonCard } from './sub-lesson-card';

interface SubLessonsSectionProps {
  parentId: string;
  onNavigateToSubLesson: (subLessonId: string) => void;
}

export const SubLessonsSection = ({ parentId, onNavigateToSubLesson }: SubLessonsSectionProps) => {
  const { data: subLessons, isLoading: isLoadingSubLessons } = useGetSubLessons(parentId);
  const { data: categories, isLoading: isLoadingCategories } = useGetCategories(parentId);

  if (isLoadingSubLessons || isLoadingCategories) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const publishedSubLessons = subLessons?.filter(l => l.status === LessonStatus.PUBLISHED) || [];

  if (publishedSubLessons.length === 0) {
    return <Alert severity="info">No sub-lessons available yet.</Alert>;
  }

  const sortedCategories = [...(categories || [])].sort((a, b) => a.position - b.position);
  const categorizedLessons = new Map<string | null, TLessonBase[]>();

  for (const cat of sortedCategories) {
    categorizedLessons.set(cat.id, []);
  }
  categorizedLessons.set(null, []);

  for (const lesson of publishedSubLessons) {
    const catId = lesson.categoryId || null;
    if (!categorizedLessons.has(catId)) {
      categorizedLessons.set(null, [...(categorizedLessons.get(null) || []), lesson]);
    } else {
      categorizedLessons.get(catId)!.push(lesson);
    }
  }

  const renderOrder: Array<{ categoryId: string | null; name: string; lessons: TLessonBase[] }> = [];
  for (const cat of sortedCategories) {
    const lessons = categorizedLessons.get(cat.id) || [];
    if (lessons.length > 0) {
      renderOrder.push({ categoryId: cat.id, name: cat.name, lessons });
    }
  }
  const uncategorized = categorizedLessons.get(null) || [];
  if (uncategorized.length > 0) {
    renderOrder.push({ categoryId: null, name: 'Uncategorized', lessons: uncategorized });
  }

  const hasCategories = sortedCategories.length > 0;

  let globalIndex = 0;

  return (
    <Box px={3} py={3}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        Sub-Lessons ({publishedSubLessons.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Complete each sub-lesson to finish this section
      </Typography>
      <Stack spacing={3}>
        {renderOrder.map(group => (
          <Box key={group.categoryId || 'uncategorized'}>
            {hasCategories && (
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  mb: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                {group.name}
              </Typography>
            )}
            <Stack spacing={2}>
              {group.lessons.map(subLesson => {
                const index = globalIndex++;
                return (
                  <SubLessonCard
                    key={subLesson.id}
                    lesson={subLesson}
                    index={index}
                    onClick={() => onNavigateToSubLesson(subLesson.id)}
                  />
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
